import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X, MessageCircle } from "lucide-react";
import { Logo, FarevaultLogo } from "../Logo";
import { track } from "../../lib/api";
import { b2cPath, isB2CHost, mainSiteUrl } from "../../lib/host";
import { useSettings } from "../../context/SettingsContext";

const NAV = [
  { to: "/deals", label: "Deals by Airline" },
  { to: "/popular-routes", label: "Popular Routes" },
];

export const WhatsAppFloat = ({ page }) => {
  const { settings } = useSettings();
  if (!settings?.whatsapp_number) return null;
  const href = `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent("Hi Sellvago, I need help with a flight booking.")}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="whatsapp-float-btn"
      onClick={() => track("whatsapp_click", page)}
      className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] shadow-lg flex items-center justify-center hover:scale-105 transition-transform duration-200"
      aria-label="WhatsApp support"
    >
      <MessageCircle className="w-7 h-7 text-white" />
    </a>
  );
};

export const B2CLayout = ({ children, page = "b2c" }) => {
  const [open, setOpen] = useState(false);
  const { settings } = useSettings();

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      {settings?.b2c_banner_active && settings?.b2c_banner_text && (
        <div className="bg-[#B01115] text-white text-center text-xs sm:text-sm font-semibold py-2 px-4 marquee-banner" data-testid="b2c-promo-banner">
          {settings.b2c_banner_text}
        </div>
      )}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#040B16]/95 border-b border-[#1E293B]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to={b2cPath("") || "/"} data-testid="b2c-logo-link">
            <FarevaultLogo compact />
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={b2cPath(n.to)}
                data-testid={`b2c-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors duration-200 ${isActive ? "text-[#00E5FF]" : "text-slate-300 hover:text-white"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            {isB2CHost() ? (
              <a href={mainSiteUrl()} data-testid="b2c-nav-b2b-link" className="text-sm text-slate-400 hover:text-white transition-colors duration-200">
                For Travel Agents
              </a>
            ) : (
              <Link to="/" data-testid="b2c-nav-b2b-link" className="text-sm text-slate-400 hover:text-white transition-colors duration-200">
                For Travel Agents
              </Link>
            )}
          </nav>
          <button data-testid="b2c-mobile-menu-btn" className="md:hidden text-white" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden bg-[#0B1527] border-t border-[#1E293B] px-4 py-4 space-y-3" data-testid="b2c-mobile-menu">
            {NAV.map((n) => (
              <Link key={n.to} to={b2cPath(n.to)} onClick={() => setOpen(false)} className="block text-slate-200 py-1">
                {n.label}
              </Link>
            ))}
            <a href={mainSiteUrl()} onClick={() => setOpen(false)} className="block text-slate-200 py-1">
              For Travel Agents
            </a>
          </div>
        )}
      </header>
      <main>{children}</main>
      <WhatsAppFloat page={page} />
      <footer className="bg-[#040B16] border-t border-[#1E293B] mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid sm:grid-cols-3 gap-10">
          <div>
            <FarevaultLogo compact className="mb-3" />
            <p className="text-sm text-slate-400 leading-relaxed">
              Wholesale airline fares, opened to the public — powered by Sellvago's multi-supplier engine. Search, compare, book.
            </p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Airlines</p>
            <div className="space-y-1.5">
              {["emirates", "qatar-airways", "turkish-airlines", "pia", "saudia"].map((s) => (
                <Link key={s} to={b2cPath(`/${s}`)} className="block text-sm text-slate-400 hover:text-[#00E5FF] transition-colors duration-200 capitalize">
                  {s.replace(/-/g, " ")}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Popular Routes</p>
            <div className="space-y-1.5">
              {["karachi-to-dubai", "lahore-to-jeddah", "islamabad-to-doha"].map((s) => (
                <Link key={s} to={b2cPath(`/${s}`)} className="block text-sm text-slate-400 hover:text-[#00E5FF] transition-colors duration-200 capitalize">
                  {s.replace(/-/g, " ")}
                </Link>
              ))}
            </div>
          </div>
        </div>
        <div className="border-t border-[#1E293B] py-5">
          <div className="text-center pt-2">
            <p className="text-center text-xs text-slate-500">© {new Date().getFullYear()} Sellvago · Farevault</p>
            <p className="text-center text-xs text-slate-500 mt-1">
              <Link to="/privacy" data-testid="b2c-footer-privacy" className="hover:text-slate-300 transition-colors duration-200">Privacy</Link>
              <span className="mx-2">·</span>
              <Link to="/terms" data-testid="b2c-footer-terms" className="hover:text-slate-300 transition-colors duration-200">Terms</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
